import { GoogleSignInCallback } from "@/routes/GoogleSignInCallback";
import { lazy, Suspense } from "react";
import { matchPath, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
const loadHome = () => import("@/routes/Home").then(m => ({ default: m.Home }));
const Home = lazy(loadHome);
const loadLogin = () => import("@/routes/Login").then(m => ({ default: m.Login }));
const Login = lazy(loadLogin);
const loadSignup = () => import("@/routes/Signup").then(m => ({ default: m.Signup }));
const Signup = lazy(loadSignup);
const loadForgotPassword = () => import("@/routes/ForgotPassword").then(m => ({ default: m.ForgotPassword }));
const ForgotPassword = lazy(loadForgotPassword);
const loadSites = () => import("@/routes/Sites").then(m => ({ default: m.Sites }));
const Sites = lazy(loadSites);
const loadProjects = () => import("@/routes/Projects").then(m => ({ default: m.Projects }));
const Projects = lazy(loadProjects);
const loadProjectDetail = () => import("@/routes/ProjectDetail").then(m => ({ default: m.ProjectDetail }));
const ProjectDetail = lazy(loadProjectDetail);
const loadNewSite = () => import("@/routes/NewSite").then(m => ({ default: m.NewSite }));
const ExistingSiteConnection = lazy(loadNewSite);
// /sites/new: CreateIdea, or the scan onboarding for a browser that opted in with ?onboarding=scan.
const loadNewSiteChooser = () => import("@/features/onboarding/NewSiteChooser");
const NewSite = lazy(loadNewSiteChooser);
const Businesses = lazy(() => import("@/routes/Businesses"));
const DomainSettings = lazy(() => import("@/features/portfolio/DomainSettings"));
const SecuritySettings = lazy(() => import("@/routes/SecuritySettings").then(m => ({ default: m.SecuritySettings })));
const IdeaRedirect = lazy(() => import("@/routes/SitePage").then(m => ({ default: m.IdeaRedirect })));
// /sites/:ref: SiteSettings for a Xano site, the builder's page for a built one (routes/SitePage.tsx).
const loadSitePage = () => import("@/routes/SitePage").then(m => ({ default: m.SitePage }));
const SitePage = lazy(loadSitePage);
// /sites/:ref/editor: the page editor full screen, outside the shell (features/site-workspace/EditorRoute.tsx).
const loadSiteEditor = () => import("@/features/site-workspace/EditorRoute");
const SiteEditor = lazy(loadSiteEditor);
// /sites/:ref/review: Review › Approvals full screen, outside the shell (features/site-workspace/ReviewRoute.tsx).
const loadSiteReview = () => import("@/features/site-workspace/ReviewRoute");
const SiteReview = lazy(loadSiteReview);
const loadPreserveSite = () => import("@/routes/PreserveSite").then(m => ({ default: m.PreserveSite }));
const PreserveSite = lazy(loadPreserveSite);
const loadPages = () => import("@/routes/Pages").then(m => ({ default: m.Pages }));
const Pages = lazy(loadPages);
const loadPagesAll = () => import("@/routes/PagesAll").then(m => ({ default: m.PagesAll }));
const PagesAll = lazy(loadPagesAll);
const loadPagesKeywords = () => import("@/routes/PagesKeywords").then(m => ({ default: m.PagesKeywords }));
const PagesKeywords = lazy(loadPagesKeywords);
const loadContentReviews = () => import("@/routes/ContentReviews").then(m => ({ default: m.ContentReviews }));
const ContentReviews = lazy(loadContentReviews);
const loadPageEdit = () => import("@/routes/PageEdit").then(m => ({ default: m.PageEdit }));
const PageEdit = lazy(loadPageEdit);
const loadSchemaBackfill = () => import("@/routes/SchemaBackfill").then(m => ({ default: m.SchemaBackfill }));
const SchemaBackfill = lazy(loadSchemaBackfill);
const loadFixes = () => import("@/routes/Fixes").then(m => ({ default: m.Fixes }));
const Fixes = lazy(loadFixes);
const loadInsights = () => import("@/routes/Insights").then(m => ({ default: m.Insights }));
const Insights = lazy(loadInsights);
const InstallationPage=lazy(()=>import("@/features/replay-installation/InstallationPage"));
const SessionDetail=lazy(()=>import("@/features/session-replay/SessionDetail"));
const RawReplayPage=lazy(()=>import("@/features/replay-raw/RawReplayPage"));
// /analytics is the Analytics home: one picker, the tabs, and the old screens mounted inside it.
const AnalyticsHome=lazy(()=>import("@/features/analytics-home/AnalyticsHome"));
const SocialHandoff=lazy(()=>import('./routes/SocialHandoff').then(m=>({default:m.SocialHandoff})));
const SocialOperations = lazy(()=>import("@/routes/SocialOperations").then(m=>({default:m.SocialOperations})));
const loadSocialPublishing = () => import("@/routes/SocialPublishing").then(m => ({default:m.SocialPublishing}));
const SocialPublishing = lazy(loadSocialPublishing);
const loadIntegrationsSocialCallback = () => import("@/routes/IntegrationsSocialCallback").then(m => ({ default: m.IntegrationsSocialCallback }));
const IntegrationsSocialCallback = lazy(loadIntegrationsSocialCallback);
const loadIntegrations = () => import("@/routes/Integrations").then(m => ({ default: m.Integrations }));
const Integrations = lazy(loadIntegrations);
// AI Visibility: the one page for it. /insights/visibility* redirects here (InsightsGate).
// /keywords folded into keyword research: the old address redirects, mapping ?site= to its business.
const loadKeywords = () => import("@/features/keywords/LegacyRedirect");
const Keywords = lazy(loadKeywords);
const loadKeywordResearch = () => import("@/features/keywords/KeywordsPage");
const Scheduling = lazy(() => import("@/routes/Scheduling").then(m => ({default:m.Scheduling})));
const loadSubmissions = () => import("@/routes/Submissions").then(m => ({ default: m.Submissions }));
const Submissions = lazy(loadSubmissions);
const loadContent = () => import("@/routes/Content").then(m => ({ default: m.Content }));
const Content = lazy(loadContent);
const loadBilling = () => import("@/routes/Billing").then(m => ({ default: m.Billing }));
const Billing = lazy(loadBilling);
const loadConnectedApps = () => import("@/routes/ConnectedApps").then(m => ({ default: m.ConnectedApps }));
const ConnectedApps = lazy(loadConnectedApps);
const loadFeeds = () => import("@/routes/Feeds").then(m => ({ default: m.Feeds }));
const Feeds = lazy(loadFeeds);
const loadTables = () => import("@/routes/Tables").then(m => ({ default: m.Tables }));
const Tables = lazy(loadTables);
const loadConnectedAppsPreview = () => import("@/routes/dev/ConnectedAppsPreview").then(m => ({ default: m.ConnectedAppsPreview }));
const ConnectedAppsPreview = lazy(loadConnectedAppsPreview);
const loadFeedsPreview = () => import("@/routes/dev/FeedsPreview").then(m => ({ default: m.FeedsPreview }));
const FeedsPreview = lazy(loadFeedsPreview);
const loadBillingPreview = () => import("@/routes/dev/BillingPreview").then(m => ({ default: m.BillingPreview }));
const BillingPreview = lazy(loadBillingPreview);
const loadHomePreview = () => import("@/routes/dev/HomePreview").then(m => ({ default: m.HomePreview }));
const HomePreview = lazy(loadHomePreview);
const loadRecordPreview = () => import("@/routes/dev/RecordPreview").then(m => ({ default: m.RecordPreview }));
const RecordPreview = lazy(loadRecordPreview);
const loadGridPreview = () => import("@/routes/dev/GridPreview").then(m => ({ default: m.GridPreview }));
const GridPreview = lazy(loadGridPreview);
const loadNavPreview = () => import("@/routes/dev/NavPreview").then(m => ({ default: m.NavPreview }));
const NavPreview = lazy(loadNavPreview);
const loadSectionsPreview = () => import("@/routes/dev/SectionsPreview").then(m => ({ default: m.SectionsPreview }));
const SectionsPreview = lazy(loadSectionsPreview);
const QuizPreview = lazy(() => import("@/routes/dev/QuizPreview").then(m => ({ default: m.QuizPreview })));
const loadInterfacesPreview = () => import("@/routes/dev/InterfacesPreview").then(m => ({ default: m.InterfacesPreview }));
const loadLinksPreview = () => import("@/routes/dev/LinksPreview").then(m => ({ default: m.LinksPreview }));
const LinksPreview = lazy(loadLinksPreview);
const WorkPreview = lazy(() => import("@/routes/dev/WorkPreview").then(m => ({ default: m.WorkPreview })));
const WorkEditPreview = lazy(() => import("@/routes/dev/WorkPreview").then(m => ({ default: m.WorkEditPreview })));
const InterfacesPreview = lazy(loadInterfacesPreview);
const loadFixesPreview = () => import("@/routes/dev/FixesPreview").then(m => ({ default: m.FixesPreview }));
const FixesPreview = lazy(loadFixesPreview);
const loadSourceMarkPreview = () => import("@/routes/dev/SourceMarkPreview").then(m => ({ default: m.SourceMarkPreview }));
const SourceMarkPreview = lazy(loadSourceMarkPreview);
const loadProjectsPreview = () => import("@/routes/dev/ProjectsPreview").then(m => ({ default: m.ProjectsPreview }));
const ProjectsPreview = lazy(loadProjectsPreview);
const loadNewSitePreview = () => import("@/routes/dev/NewSitePreview").then(m => ({ default: m.NewSitePreview }));
const loadTrackingPreview = () => import("@/routes/dev/TrackingPreview").then(m => ({ default: m.TrackingPreview }));
const loadConnectionsPreview = () => import("@/routes/dev/ConnectionsPreview").then(m => ({ default: m.ConnectionsPreview }));
const ConnectionsPreview = lazy(loadConnectionsPreview);
const loadConnections = () => import("@/routes/Connections").then(m => ({ default: m.Connections }));
const Connections = lazy(loadConnections);
const TrackingPreview = lazy(loadTrackingPreview);
const loadSiteTracking = () => import("@/routes/SiteTracking").then(m => ({ default: m.SiteTracking }));
const loadSiteGoals = () => import("@/routes/SiteGoals").then(m => ({ default: m.SiteGoals }));
const loadAllSites = () => import("@/routes/AllSites").then(m => ({ default: m.AllSites }));
const AllSites = lazy(loadAllSites);
const loadAllSitesPreview = () => import("@/routes/dev/AllSitesPreview").then(m => ({ default: m.AllSitesPreview }));
const AllSitesPreview = lazy(loadAllSitesPreview);
const SiteGoals = lazy(loadSiteGoals);
const loadGoalsPreview = () => import("@/routes/dev/GoalsPreview").then(m => ({ default: m.GoalsPreview }));
const GoalsPreview = lazy(loadGoalsPreview);
const loadTasks = () => import("@/routes/Tasks").then(m => ({ default: m.Tasks }));
const Tasks = lazy(loadTasks);
const loadTasksPreview = () => import("@/routes/dev/TasksPreview").then(m => ({ default: m.TasksPreview }));
const TasksPreview = lazy(loadTasksPreview);
const SiteTracking = lazy(loadSiteTracking);
const NewSitePreview = lazy(loadNewSitePreview);
const loadSharingPreview = () => import("@/routes/dev/SharingPreview").then(m => ({ default: m.SharingPreview }));
const SharingPreview = lazy(loadSharingPreview);
const EmailPreview = lazy(() => import("@/routes/dev/EmailPreview").then(m => ({ default: m.EmailPreview })));
const loadEmptyStatePreview = () => import("@/routes/dev/EmptyStatePreview").then(m => ({ default: m.EmptyStatePreview }));
const EmptyStatePreview = lazy(loadEmptyStatePreview);
const loadSkeletonPreview = () => import("@/routes/dev/SkeletonPreview").then(m => ({ default: m.SkeletonPreview }));
const PublishAtPreview = lazy(() => import("@/routes/dev/PublishAtPreview").then(m => ({ default: m.PublishAtPreview })));
const SkeletonPreview = lazy(loadSkeletonPreview);
const loadIntegrationsGoogleCallback = () => import("@/routes/IntegrationsGoogleCallback").then(m => ({ default: m.IntegrationsGoogleCallback }));
const IntegrationsGoogleCallback = lazy(loadIntegrationsGoogleCallback);
import { Layout } from "@/components/Layout";
import { RequireAuth } from "@/components/RequireAuth";
import { ScopeProvider } from "@/lib/scope";
import { InsightsRedirect, ToAnalytics } from "@/features/analytics-home/redirects";
const loadInterfaces = () => import("@/routes/Interfaces").then(m => ({ default: m.Interfaces }));
const Interfaces = lazy(loadInterfaces);
const loadLinks = () => import("@/routes/Links").then(m => ({ default: m.Links }));
const Links = lazy(loadLinks);
const loadWork = () => import("@/routes/Work").then(m => ({ default: m.Work }));
const Work = lazy(loadWork);
const loadWorkEdit = () => import("@/routes/WorkEdit").then(m => ({ default: m.WorkEdit }));
const WorkEdit = lazy(loadWorkEdit);
import { SkeletonPanel } from "@/components/ui/skeleton";

// STUDIO IS LAZY because its media library (mediabunny, the demuxer and muxer
// behind recording, transcription and export) is most of a megabyte, and every
// other screen in the admin would otherwise download it on first load.
const Guides = lazy(() => import("@/routes/Guides").then((m) => ({ default: m.Guides })));
const GuideDetail = lazy(() => import("@/routes/GuideDetail").then((m) => ({ default: m.GuideDetail })));
const GuidesPreview = lazy(() => import("@/routes/GuidesPreview").then((m) => ({ default: m.GuidesPreview })));
const FeedbackPreview = lazy(() => import("@/routes/FeedbackPreview").then((m) => ({ default: m.FeedbackPreview })));
const Studio = lazy(() => import("@/routes/Studio").then((m) => ({ default: m.Studio })));
const StudioEdit = lazy(() => import("@/routes/StudioEdit").then((m) => ({ default: m.StudioEdit })));
const StudioPreview = lazy(() => import("@/routes/dev/StudioPreview").then((m) => ({ default: m.StudioPreview })));
const MediaPreview = lazy(() => import("@/routes/dev/MediaPreview").then((m) => ({ default: m.MediaPreview })));
const Brands = lazy(() => import("@/routes/media/Brands").then((m) => ({ default: m.Brands })));
const BrandEdit = lazy(() => import("@/routes/media/BrandEdit").then((m) => ({ default: m.BrandEdit })));
const MediaCreate = lazy(() => import("@/routes/media/Create").then((m) => ({ default: m.Create })));
const MediaLibrary = lazy(() => import("@/routes/media/Library").then((m) => ({ default: m.Library })));
const studioFallback = <SkeletonPanel className="h-64" />;

// Dev-only fixture harness for the section parser.
// Lazy so the parser module + ~120KB of fixture HTML never enters the boot path.
const ParserCheck = lazy(() =>
  import("@/routes/dev/ParserCheck").then((m) => ({ default: m.ParserCheck }))
);

const initialRoutes = [
  { path: "/login", load: loadLogin },
  { path: "/signup", load: loadSignup },
  { path: "/forgot-password", load: loadForgotPassword },
  { path: "/", load: loadSites },
  { path: "/sites", load: loadSites },
  { path: "/overview", load: loadHome },
  { path: "/projects", load: loadProjects },
  { path: "/projects/:projectId", load: loadProjectDetail },
  { path: "/sites/new", load: loadNewSiteChooser },
  { path: "/sites/connect", load: loadNewSite },
  { path: "/sites/:siteId", load: loadSitePage },
  { path: "/sites/:siteId/editor", load: loadSiteEditor },
  { path: "/sites/:siteId/review", load: loadSiteReview },
  { path: "/pages", load: loadPagesAll },
  { path: "/pages/keywords", load: loadPagesKeywords },
  { path: "/pages/reviews", load: loadContentReviews },
  { path: "/sites/:siteId/pages/import", load: loadPreserveSite },
  { path: "/sites/:siteId/pages", load: loadPages },
  { path: "/sites/:siteId/tracking", load: loadSiteTracking },
  { path: "/sites/:siteId/goals", load: loadSiteGoals },
  { path: "/tasks", load: loadTasks },
  { path: "/all-sites", load: loadAllSites },
  { path: "/sites/:siteId/pages/:pageId/edit", load: loadPageEdit },
  { path: "/insights", load: loadInsights },
  { path: "/insights/:tab", load: loadInsights },
  { path: "/insights/:tab/:siteId", load: loadInsights },
  { path: "/keywords", load: loadKeywords },
  { path: "/keywords/research", load: loadKeywordResearch },
  { path: "/submissions", load: loadSubmissions },
  { path: "/content", load: loadContent },
  { path: "/billing", load: loadBilling },
  { path: "/connected-apps", load: loadConnectedApps },
  { path: "/feeds", load: loadFeeds },
  { path: "/tables", load: loadTables },
  { path: "/tables/:workspaceId", load: loadTables },
  { path: "/tables/:workspaceId/:tableId", load: loadTables },
  { path: "/interfaces", load: loadInterfaces },
  { path: "/links", load: loadLinks },
  { path: "/work", load: loadWork },
  { path: "/work/:workspaceId/:documentId", load: loadWorkEdit },
  { path: "/fixes", load: loadFixes },
  { path: "/integrations/social/callback", load: loadIntegrationsSocialCallback },
  { path: "/connections", load: loadConnections },
  { path: "/integrations/classic", load: loadIntegrations },
  { path: "/integrations/google/callback", load: loadIntegrationsGoogleCallback },
  { path: "/tools/schema-backfill", load: loadSchemaBackfill },
];

// Start route code before the auth effect; private data still waits for verification.
if (!window.location.hostname.endsWith(".pages.dev")) {
  const route = initialRoutes.find(r => matchPath({ path: r.path, end: true }, window.location.pathname));
  if (route) void route.load().catch(() => {});
}

export default function App() {
  return (
    <Routes>
      <Route element={<Suspense fallback={studioFallback}><Outlet /></Suspense>}>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google/callback" element={<GoogleSignInCallback />} />
      <Route path="/integrations/google/callback" element={<IntegrationsGoogleCallback />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {import.meta.env.DEV && (
        <Route
          path="/dev/parser-check"
          element={
            <Suspense fallback={<div className="p-8 text-sm text-muted-foreground">Loading parser harness…</div>}>
              <ParserCheck />
            </Suspense>
          }
        />
      )}

      {import.meta.env.DEV && (
        <Route path="/dev/billing" element={<BillingPreview />} />
      )}

      {import.meta.env.DEV && (
        <Route path="/dev/connected-apps" element={<ConnectedAppsPreview />} />
      )}

      {import.meta.env.DEV && <Route path="/dev/feeds" element={<FeedsPreview />} />}

      {import.meta.env.DEV && <Route path="/dev/home" element={<HomePreview />} />}

      {/* The record view, every shape, no sign-in. See RecordPreview. */}
      {import.meta.env.DEV && <Route path="/dev/record" element={<RecordPreview />} />}

      {import.meta.env.DEV && <Route path="/dev/source" element={<SourceMarkPreview />} />}

      {/* The grid itself, on fixtures. ?rows=200 exercises the virtualiser. */}
      {import.meta.env.DEV && <Route path="/dev/grid" element={<GridPreview />} />}

      {/* The chrome: four-group sidebar + scope selector, on a primed cache. */}
      {import.meta.env.DEV && <Route path="/dev/nav/*" element={<NavPreview studio={<Suspense fallback={studioFallback}><Studio /></Suspense>} />} />}

      {/* The sections editor, beside the HTML it emits. See SectionsPreview. */}
      {import.meta.env.DEV && <Route path="/dev/sections" element={<SectionsPreview />} />}
      {/* The quiz form editor on a branching quiz, beside its JSON. See QuizPreview. */}
      {import.meta.env.DEV && <Route path="/dev/quiz" element={<QuizPreview />} />}

      {/* The Studio transcript editor on fixture words. ?aspect=9:16, ?video=<url>. */}
      {import.meta.env.DEV && <Route path="/dev/studio" element={<Suspense fallback={studioFallback}><StudioPreview /></Suspense>} />}
      {/* Every Media template and mark on real fleet brands. ?brand=<domain>, ?marks=1. */}
      {import.meta.env.DEV && <Route path="/dev/media" element={<Suspense fallback={studioFallback}><MediaPreview /></Suspense>} />}
      {/* The feedback recorder, on real controls. It lives inside the authenticated
          shell, so this is the only way to look at it signed out. */}
      {import.meta.env.DEV && <Route path="/dev/feedback" element={<Suspense fallback={studioFallback}><FeedbackPreview /></Suspense>} />}
      {/* The capture view on a fixture: the real one needs an account and a recording. */}
      {import.meta.env.DEV && <Route path="/dev/guides" element={<Suspense fallback={studioFallback}><GuidesPreview /></Suspense>} />}

      {/* The Interfaces list. ?empty=1 for the empty state. */}
      {import.meta.env.DEV && <Route path="/dev/interfaces" element={<InterfacesPreview />} />}
      {/* Every kind of link and every state it can be in. ?empty=1 for the empty state. */}
      {import.meta.env.DEV && <Route path="/dev/links" element={<LinksPreview />} />}
      {/* The site Email tab in every state, on a fake bloxx-mail. ?state=live for one. */}
      {import.meta.env.DEV && <Route path="/dev/email" element={<EmailPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/work" element={<WorkPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/work/:workspaceId/:documentId" element={<WorkEditPreview />} />}

      {/* The fixes list, all three statuses at once. ?role=editor for the
          version somebody who cannot apply one sees. */}
      {import.meta.env.DEV && <Route path="/dev/fixes" element={<FixesPreview />} />}

      {/* Both paths, because ProjectDetail reads its id from useParams. */}
      {import.meta.env.DEV && <Route path="/dev/new-site" element={<NewSitePreview />} />}
      {import.meta.env.DEV && <Route path="/dev/tracking" element={<TrackingPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/goals" element={<GoalsPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/tasks" element={<TasksPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/all-sites" element={<AllSitesPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/connections" element={<ConnectionsPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/sharing" element={<SharingPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/empty-state" element={<EmptyStatePreview />} />}
      {import.meta.env.DEV && <Route path="/dev/skeletons" element={<SkeletonPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/publish-at" element={<PublishAtPreview />} />}
      {import.meta.env.DEV && <Route path="/dev/projects" element={<ProjectsPreview />} />}
      {import.meta.env.DEV && (
        <Route path="/dev/projects/:projectId" element={<ProjectsPreview />} />
      )}

      {/* The editor takes the whole window: signed in, but outside the shell (no rail, site header or tabs). */}
      <Route path="/sites/:siteId/editor" element={<RequireAuth><SiteEditor /></RequireAuth>} />
      <Route path="/sites/:siteId/review" element={<RequireAuth><SiteReview /></RequireAuth>} />

      <Route
        element={
          <RequireAuth>
            {/* INSIDE RequireAuth, so the workspace list is never asked for
                before there is a session to ask with. */}
            <ScopeProvider>
              <Layout />
            </ScopeProvider>
          </RequireAuth>
        }
      >
        {/* "/" IS THE SITES LIST. Sites is the first row of the sidebar and where
            Login and Signup already send people, so the bare domain lands on the
            same screen as signing in does. Overview (live readiness scans of
            every origin) stays one click away at /overview. */}
        <Route path="/" element={<Navigate to="/sites" replace />} />
        <Route path="/overview" element={<Home />} />
        <Route path="/sites" element={<Sites />} />
        {/* Where the sites list lived while /sites showed the Ideas portfolio. */}
        <Route path="/sites/legacy" element={<Navigate to="/sites" replace />} />
        <Route path="/sites/connect" element={<ExistingSiteConnection />} />
        <Route path="/settings/domains" element={<DomainSettings />} />
        <Route path="/settings/security" element={<SecuritySettings />} />
        <Route path="/businesses" element={<Businesses />} />
        {/* ONE WEBSITE MODEL: a website is a site, whatever made it. Every /ideas address is a redirect into
            /sites, so links already sent still land. Batch, compare and the parent's brand list are retired (WS5):
            a parent's brands are on its own site page. */}
        <Route path="/ideas" element={<Navigate to="/sites" replace />} />
        <Route path="/ideas/batch" element={<Navigate to="/sites" replace />} />
        <Route path="/ideas/compare" element={<Navigate to="/sites" replace />} />
        <Route path="/ideas/parent" element={<RetiredParentPortfolio />} />
        <Route path="/ideas/:id" element={<IdeaRedirect />} />
        <Route path="/ideas/:id/workspace" element={<IdeaRedirect />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:projectId" element={<ProjectDetail />} />
        <Route path="/sites/new" element={<NewSite />} />
        <Route path="/sites/:siteId" element={<SitePage />} />
        <Route path="/sites/:siteId/tracking" element={<SiteTracking />} />
        <Route path="/all-sites" element={<AllSites />} />
        <Route path="/sites/:siteId/goals" element={<SiteGoals />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/pages" element={<PagesAll />} />
        <Route path="/pages/keywords" element={<PagesKeywords />} />
        <Route path="/pages/reviews" element={<ContentReviews />} />
        <Route path="/sites/:siteId/pages/import" element={<PreserveSite />} />
        <Route path="/sites/:siteId/pages" element={<Pages />} />
        <Route path="/sites/:siteId/pages/:pageId/edit" element={<PageEdit />} />
        {/* THE TAB IS IN THE PATH. Each analytics view is its own sidebar item
            under the IA regroup, and a nav item has to be a link. `/insights`
            alone lands on the default; `/insights/12` is a pre-regroup link and
            Insights redirects it (see the legacySiteId note there). */}
        <Route path="/insights/sessions/:siteId/:sessionId" element={<Suspense fallback={<div role="status">Loading session…</div>}><SessionDetail /></Suspense>} />
        <Route path="/insights/recording/:siteId/install" element={<Suspense fallback={<div role="status">Loading installation…</div>}><InstallationPage /></Suspense>} />
        {/* The old Analytics addresses are tabs of the home now; the site and the screen's params carry over. */}
        <Route path="/insights/sessions" element={<ToAnalytics tab="recordings" siteFrom="site" />} />
        <Route path="/insights/journeys" element={<ToAnalytics tab="recordings" sub="paths" siteFrom="site" />} />
        <Route path="/session-replay" element={<ToAnalytics tab="recordings" siteFrom="site" />} />
        <Route path="/insights/replay/:siteId/:sessionId" element={<Suspense fallback={<div role="status">Loading recording…</div>}><RawReplayPage /></Suspense>} />
        <Route path="/analytics" element={<Suspense fallback={<div role="status">Loading analytics…</div>}><AnalyticsHome /></Suspense>} />
        <Route path="/experiments" element={<ToAnalytics tab="experiments" siteFrom="site" />} />
        <Route path="/insights" element={<InsightsGate />} />
        <Route path="/insights/:tab" element={<InsightsGate />} />
        <Route path="/insights/:tab/:siteId" element={<InsightsGate />} />
        <Route path="/keywords" element={<Suspense fallback={<div role="status">Opening keyword research…</div>}><Keywords /></Suspense>} />
        <Route path="/keywords/research" element={<ToAnalytics tab="search" sub="keywords" siteFrom="profile" />} />
        <Route path="/ai-visibility" element={<ToAnalytics tab="ai" siteFrom="site" />} />
        <Route path="/scheduling" element={<Scheduling />} />
        <Route path="/submissions" element={<Submissions />} />
        <Route path="/content" element={<Content />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/connected-apps" element={<ConnectedApps />} />
        <Route path="/feeds" element={<Feeds />} />
        {/* The workspace and table are in the URL so a grid is linkable, which is
            the thing the nested accordions on /feeds cannot be. */}
        <Route path="/tables" element={<Tables />} />
        <Route path="/tables/:workspaceId" element={<Tables />} />
        <Route path="/tables/:workspaceId/:tableId" element={<Tables />} />
        <Route path="/interfaces" element={<Interfaces />} />
        <Route path="/social/handoff/:handoffId" element={<Suspense fallback={studioFallback}><SocialHandoff /></Suspense>} />
        <Route path="/social/operations" element={<Suspense fallback={studioFallback}><SocialOperations /></Suspense>} />
        <Route path="/social" element={<Suspense fallback={studioFallback}><SocialPublishing /></Suspense>} />
        <Route path="/social/:contentId" element={<Suspense fallback={studioFallback}><SocialPublishing /></Suspense>} />
        <Route path="/content-performance" element={<ToAnalytics tab="content" sub="social" siteFrom="business" />} />
        <Route path="/content-performance/:contentId" element={<Suspense fallback={studioFallback}><SocialPublishing /></Suspense>} />
        <Route path="/links" element={<Links />} />
        <Route path="/work" element={<Work />} />
        <Route path="/work/:workspaceId/:documentId" element={<Suspense fallback={studioFallback}><WorkEdit /></Suspense>} />
        {/* What somebody did, kept: feedback while it is being fixed, a guide
            once it is tidy. See src/captures.ts. */}
        <Route path="/guides" element={<Suspense fallback={studioFallback}><Guides /></Suspense>} />
        <Route path="/guides/:workspaceId/:captureId" element={<Suspense fallback={studioFallback}><GuideDetail /></Suspense>} />
        <Route path="/studio" element={<Suspense fallback={studioFallback}><Studio /></Suspense>} />
        <Route path="/studio/:workspaceId/:recordingId" element={<Suspense fallback={studioFallback}><StudioEdit /></Suspense>} />
        <Route path="/media/brands" element={<Suspense fallback={studioFallback}><Brands /></Suspense>} />
        <Route path="/media/brands/:workspaceId/:brandId" element={<Suspense fallback={studioFallback}><BrandEdit /></Suspense>} />
        <Route path="/media/create" element={<Suspense fallback={studioFallback}><MediaCreate /></Suspense>} />
        <Route path="/media/library" element={<Suspense fallback={studioFallback}><MediaLibrary /></Suspense>} />
        <Route path="/fixes" element={<Fixes />} />
        <Route path="/integrations/social/callback" element={<IntegrationsSocialCallback />} />
        <Route path="/connections" element={<Connections />} />
        {/* /integrations became /connections (Direction A, P2); query and hash survive, so OAuth returns and old links still land. */}
        <Route path="/integrations" element={<RedirectToConnections />} />
        <Route path="/integrations/classic" element={<Integrations />} />

        <Route path="/tools/schema-backfill" element={<SchemaBackfill />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Search Console, Google Analytics, Assistants, bots and visibility are tabs of the Analytics home
 * (features/analytics-home/redirects.tsx); old links land there with their site. Audit keeps this page. */
function InsightsGate() {
  return <InsightsRedirect keep={<Insights />} />;
}

/** /ideas/parent?journey=<id>: the parent's own site page, which lists its brands. Any other form: the site list. */
function RetiredParentPortfolio() {
  const parent = new URLSearchParams(useLocation().search).get("journey");
  return <Navigate to={parent ? `/sites/${encodeURIComponent(parent)}` : "/sites"} replace />;
}

function RedirectToConnections() {
  const { search, hash } = useLocation();
  return <Navigate to={`/connections${search}${hash}`} replace />;
}
